import { useState, useEffect, useRef } from 'react';
import {
  Card, Row, Col, Input, InputNumber, Button, Select, Space, Tag,
  Alert, Spin, Descriptions, message, Divider, Modal, Statistic,
  Typography, Segmented
} from 'antd';
import {
  SearchOutlined, UserOutlined, BookOutlined, SafetyCertificateOutlined,
  SaveOutlined, CheckCircleOutlined, ExclamationCircleOutlined,
  ClockCircleOutlined, WarningOutlined, TrophyOutlined, IdcardOutlined,
  FontSizeOutlined
} from '@ant-design/icons';
import apiClient from '../../../api/apiClient';
import { getCicloAcademico, getAniosAcademicos } from '../../../utils/cicloEscolar';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

// ── helpers ──────────────────────────────────────────────────────────────────
const tagUnidad = (u) =>
  (u.Cerrada || u.EstaCerrada)
    ? <Tag color="red"   style={{ fontSize: 10, marginLeft: 6 }}>Cerrada</Tag>
    : <Tag color="green" style={{ fontSize: 10, marginLeft: 6 }}>Abierta</Tag>;

// ── Componente ────────────────────────────────────────────────────────────────
const ActividadesDirector = () => {
  // ── Modal hook (Ant Design 5 requiere useModal en vez del static Modal.confirm) ──
  const [modal, contextHolder] = Modal.useModal();

  // ── Modo de búsqueda ────────────────────────────────────────────────────
  const [modoBusqueda, setModoBusqueda] = useState('carnet'); // 'carnet' | 'nombre'

  // ── Búsqueda por carnet ─────────────────────────────────────────────────
  const [cicloEscolar, setCicloEscolar]   = useState(getCicloAcademico());
  const [carnet, setCarnet]               = useState('');
  const [buscando, setBuscando]           = useState(false);

  // ── Búsqueda por nombre ─────────────────────────────────────────────────
  const [listaAlumnos, setListaAlumnos]   = useState([]);     // todos para el Select
  const [loadingLista, setLoadingLista]   = useState(false);
  const [alumnoSel, setAlumnoSel]         = useState(null);   // IdAlumno elegido en el Select
  const listaYaCargada                    = useRef(false);

  // ── Alumno encontrado ───────────────────────────────────────────────────
  const [alumno, setAlumno]   = useState(null);   // { IdAlumno, NombreAlumno, ... }
  const [cursos, setCursos]   = useState([]);

  // ── Selecciones en cascada ──────────────────────────────────────────────
  const [cursoSel, setCursoSel]                       = useState(null);
  const [unidades, setUnidades]                       = useState([]);
  const [loadingUnidades, setLoadingUnidades]         = useState(false);

  const [unidadSel, setUnidadSel]                     = useState(null);
  const [actividades, setActividades]                 = useState([]);
  const [loadingActividades, setLoadingActividades]   = useState(false);

  const [actividadSel, setActividadSel]               = useState(null);
  // notaActual: undefined=aún no cargado, null=cargado sin nota previa, obj=tiene nota
  const [notaActual, setNotaActual]                   = useState(undefined);
  const [loadingNota, setLoadingNota]                 = useState(false);

  // ── Formulario ──────────────────────────────────────────────────────────
  const [nuevoPunteo, setNuevoPunteo]       = useState(null);
  const [observaciones, setObservaciones]   = useState('');
  const [motivo, setMotivo]                 = useState('');
  const [guardando, setGuardando]           = useState(false);

  // ── Resultado ───────────────────────────────────────────────────────────
  const [resultado, setResultado] = useState(null);

  // ── Cargar lista de alumnos (para búsqueda por nombre) ──────────────────
  const cargarListaAlumnos = async () => {
    if (listaYaCargada.current) return;
    setLoadingLista(true);
    try {
      const res = await apiClient.get('/alumnos');
      const data = Array.isArray(res.data) ? res.data : (res.data.data || []);
      setListaAlumnos(data);
      listaYaCargada.current = true;
    } catch {
      message.error('Error al cargar la lista de alumnos');
    } finally {
      setLoadingLista(false);
    }
  };

  const handleModoBusquedaChange = (modo) => {
    setModoBusqueda(modo);
    resetTodo();
    if (modo === 'nombre' && !listaYaCargada.current) cargarListaAlumnos();
  };

  // ── Cargar cursos de un alumno (común para ambos modos) ─────────────────
  const cargarCursosAlumno = async (idAlumno) => {
    setBuscando(true);
    resetDesdeAlumno();
    try {
      // Llamadas en paralelo: cursos del alumno + datos del alumno (para obtener nombre)
      const [cursosRes, alumnoRes] = await Promise.all([
        apiClient.get(`/solicitudes-calificacion/cursos-alumno/${idAlumno}`, { params: { cicloEscolar } }),
        apiClient.get(`/alumnos/${idAlumno}`),
      ]);

      const data = cursosRes.data.data || cursosRes.data || [];
      if (!data.length) {
        message.warning('No se encontraron cursos para ese alumno en el ciclo seleccionado');
        setAlumno(null);
        setCursos([]);
        return;
      }

      // Nombre viene de /alumnos/:id (Apellidos + Nombres)
      const ad = alumnoRes.data.data || alumnoRes.data || {};
      const nombreCompleto = ad.Apellidos && ad.Nombres
        ? `${ad.Apellidos}, ${ad.Nombres}`
        : (data[0].NombreAlumno || `Carnet ${idAlumno}`);

      setAlumno({
        IdAlumno:      Number(idAlumno),
        NombreAlumno:  nombreCompleto,
        Matricula:     ad.Matricula || data[0].Matricula,
        NombreGrado:   data[0].NombreGrado,
        NombreSeccion: data[0].NombreSeccion,
        NombreJornada: data[0].NombreJornada,
      });
      setCursos(data);
    } catch (e) {
      message.error(e.response?.data?.error || 'Error al buscar los cursos del alumno');
      setAlumno(null);
      setCursos([]);
    } finally {
      setBuscando(false);
    }
  };

  // ── Buscar por carnet ─────────────────────────────────────────────────────
  const buscarPorCarnet = () => {
    const id = parseInt(carnet, 10);
    if (!carnet || isNaN(id)) {
      message.warning('Ingresá un carnet válido');
      return;
    }
    cargarCursosAlumno(id);
  };

  // ── Buscar por nombre (al seleccionar en el Select) ───────────────────────
  const handleAlumnoSelectChange = (idAlumno) => {
    setAlumnoSel(idAlumno);
    if (idAlumno) cargarCursosAlumno(idAlumno);
  };

  // ── Cascada: curso ────────────────────────────────────────────────────────
  const handleCursoChange = async (idAsignacion) => {
    setCursoSel(idAsignacion);
    resetDesdeUnidad();
    setLoadingUnidades(true);
    try {
      const res = await apiClient.get(`/unidades/asignacion/${idAsignacion}`);
      setUnidades(res.data.data || res.data || []);
    } catch {
      message.error('Error al cargar unidades');
    } finally {
      setLoadingUnidades(false);
    }
  };

  // ── Cascada: unidad ───────────────────────────────────────────────────────
  const handleUnidadChange = async (idUnidad) => {
    setUnidadSel(idUnidad);
    resetDesdeActividad();
    setLoadingActividades(true);
    try {
      const res = await apiClient.get(`/actividades/unidad/${idUnidad}`);
      const todas = res.data.data || [];
      setActividades(todas.filter(a => a.Estado === true || a.Estado === 1));
    } catch {
      message.error('Error al cargar actividades');
    } finally {
      setLoadingActividades(false);
    }
  };

  // ── Cascada: actividad ────────────────────────────────────────────────────
  const handleActividadChange = async (idActividad) => {
    setActividadSel(idActividad);
    setNotaActual(undefined);   // undefined = todavía cargando
    setNuevoPunteo(null);
    setObservaciones('');
    setResultado(null);
    setMotivo('');
    setLoadingNota(true);
    try {
      const res = await apiClient.get(`/actividades/${idActividad}/alumnos`);
      const todos = res.data.data || [];
      // Comparación segura: convertir ambos a Number antes de comparar
      const encontrado = todos.find(a => Number(a.IdAlumno) === Number(alumno.IdAlumno));

      if (!encontrado) {
        // El alumno no tiene calificación registrada aún → mostramos el formulario vacío
        setNotaActual(null);   // null = cargado, sin nota previa
        setNuevoPunteo(null);
        setObservaciones('');
        return;
      }

      setNotaActual({
        IdCalificacion: encontrado.IdCalificacion,
        Punteo:         encontrado.Punteo,
        Observaciones:  encontrado.Observaciones || '',
      });
      setNuevoPunteo(encontrado.Punteo ?? null);
      setObservaciones(encontrado.Observaciones || '');
    } catch {
      message.error('Error al cargar la nota actual');
      setNotaActual(null);
    } finally {
      setLoadingNota(false);
    }
  };

  // ── Guardar ───────────────────────────────────────────────────────────────
  const confirmarGuardar = () => {
    if (nuevoPunteo === null || nuevoPunteo === undefined) {
      message.warning('Ingresá el nuevo punteo antes de guardar');
      return;
    }
    if (!motivo || motivo.trim().length < 5) {
      message.warning('El motivo es obligatorio (mínimo 5 caracteres)');
      return;
    }
    modal.confirm({
      title: 'Confirmar rectificación directa',
      icon: <ExclamationCircleOutlined style={{ color: '#fa8c16' }} />,
      content: (
        <div>
          <p>
            Vas a cambiar la nota de <strong>{alumno?.NombreAlumno}</strong>{' '}
            en <strong>{cursoActual?.NombreCurso}</strong>:
          </p>
          <p>
            Punteo actual:{' '}
            <strong>
              {notaActual?.Punteo !== null && notaActual?.Punteo !== undefined
                ? notaActual.Punteo
                : '(sin nota)'}
            </strong>{' '}
            → Nuevo punteo:{' '}
            <strong style={{ color: '#1677ff' }}>{nuevoPunteo}</strong>
          </p>
          <p style={{ color: '#888', fontSize: 12 }}>Motivo: {motivo}</p>
          <p style={{ color: '#888', fontSize: 12 }}>
            Este cambio queda registrado en la bitácora del sistema.
          </p>
        </div>
      ),
      okText: 'Sí, guardar',
      cancelText: 'Cancelar',
      onOk: guardar,
    });
  };

  const guardar = async () => {
    setGuardando(true);
    setResultado(null);
    try {
      const res = await apiClient.post('/solicitudes-calificacion/rectificar-directo', {
        IdAlumno:            alumno.IdAlumno,
        IdAsignacionDocente: cursoSel,
        IdActividad:         actividadSel,
        Punteo:              parseFloat(nuevoPunteo),
        Observaciones:       observaciones || null,
        Motivo:              motivo.trim(),
      });
      if (res.data.success) {
        setResultado(res.data.data);
        message.success('Nota rectificada correctamente');
        setNotaActual(prev => ({
          ...(prev || {}),
          Punteo:         parseFloat(nuevoPunteo),
          Observaciones:  observaciones || '',
          IdCalificacion: res.data.data.calificacion?.IdCalificacion || prev?.IdCalificacion,
        }));
        setMotivo('');
      }
    } catch (e) {
      message.error(e.response?.data?.error || 'Error al guardar la rectificación');
    } finally {
      setGuardando(false);
    }
  };

  // ── Resets ────────────────────────────────────────────────────────────────
  const resetTodo = () => {
    setCarnet('');
    setAlumnoSel(null);
    setAlumno(null);
    setCursos([]);
    resetDesdeAlumno();
  };
  const resetDesdeAlumno = () => {
    setCursoSel(null);   setUnidades([]);
    resetDesdeUnidad();
  };
  const resetDesdeUnidad = () => {
    setUnidadSel(null);  setActividades([]);
    resetDesdeActividad();
  };
  const resetDesdeActividad = () => {
    setActividadSel(null); setNotaActual(undefined);
    setNuevoPunteo(null);  setObservaciones('');
    setMotivo('');         setResultado(null);
  };

  // ── Derived values ────────────────────────────────────────────────────────
  const cursoActual     = cursos.find(c => c.IdAsignacionDocente === cursoSel);
  const actividadActual = actividades.find(a => a.IdActividad === actividadSel);
  const punteoMax       = parseFloat(actividadActual?.PunteoMaximo || 0);

  // El formulario aparece cuando la actividad está seleccionada y terminó de cargar
  // notaActual === undefined → todavía cargando
  // notaActual === null      → cargado, sin nota previa
  // notaActual === obj       → cargado, con nota existente
  const formularioCargado = actividadSel && !loadingNota && notaActual !== undefined;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <SafetyCertificateOutlined style={{ color: '#fa8c16' }} />
            Actividades — Acceso Director
          </Space>
        }
      >
        {contextHolder}

        {/* Banner modo director */}
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 24 }}
          message="Modo Administrador"
          description="Podés modificar la nota de cualquier actividad, incluso en unidades cerradas. Cada cambio queda registrado en la bitácora del sistema con tu usuario y el motivo que indiques."
        />

        {/* ══════════════════════════════════════════════════════════════════
            PASO 1 — Buscar alumno
        ══════════════════════════════════════════════════════════════════ */}
        <div style={{ marginBottom: 10, fontWeight: 600, fontSize: 13, color: '#555' }}>
          PASO 1 — Buscar alumno
        </div>

        {/* Toggle carnet / nombre */}
        <Space style={{ marginBottom: 12 }}>
          <Segmented
            options={[
              { label: <Space size={4}><IdcardOutlined />Por carnet</Space>,        value: 'carnet' },
              { label: <Space size={4}><FontSizeOutlined />Por nombre y apellido</Space>, value: 'nombre' },
            ]}
            value={modoBusqueda}
            onChange={handleModoBusquedaChange}
          />
          <Select
            value={cicloEscolar}
            onChange={val => { setCicloEscolar(val); resetTodo(); }}
            style={{ width: 90 }}
          >
            {getAniosAcademicos().map(y => (
              <Option key={y} value={y}>{y}</Option>
            ))}
          </Select>
        </Space>

        {/* — Búsqueda por carnet — */}
        {modoBusqueda === 'carnet' && (
          <Row gutter={[8, 8]} align="middle" style={{ marginBottom: 16 }}>
            <Col xs={24} sm={10} md={8} lg={6}>
              <Input
                placeholder="Carnet del alumno"
                prefix={<UserOutlined />}
                value={carnet}
                onChange={e => setCarnet(e.target.value)}
                onPressEnter={buscarPorCarnet}
                allowClear
                onClear={resetTodo}
              />
            </Col>
            <Col xs={24} sm={6} md={4} lg={3}>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={buscarPorCarnet}
                loading={buscando}
                style={{ width: '100%' }}
              >
                Buscar
              </Button>
            </Col>
            {/* Nombre inline, aparece al encontrar al alumno */}
            {alumno && (
              <Col xs={24} sm={8} md={12}>
                <Space wrap>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  <Text strong style={{ fontSize: 14 }}>{alumno.NombreAlumno}</Text>
                  <Tag color="blue" style={{ fontSize: 12 }}>Carnet: {alumno.IdAlumno}</Tag>
                  {alumno.NombreGrado && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {alumno.NombreGrado}
                      {alumno.NombreSeccion ? ` — Sección "${alumno.NombreSeccion}"` : ''}
                      {alumno.NombreJornada ? ` — ${alumno.NombreJornada}` : ''}
                    </Text>
                  )}
                </Space>
              </Col>
            )}
          </Row>
        )}

        {/* — Búsqueda por nombre — */}
        {modoBusqueda === 'nombre' && (
          <Row gutter={[8, 8]} align="middle" style={{ marginBottom: 16 }}>
            <Col xs={24} sm={16} md={12} lg={10}>
              <Select
                showSearch
                placeholder="Escribí el nombre o apellido del alumno..."
                style={{ width: '100%' }}
                loading={loadingLista}
                value={alumnoSel}
                onChange={handleAlumnoSelectChange}
                filterOption={(input, option) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase())
                }
                options={listaAlumnos.map(a => ({
                  value: a.IdAlumno,
                  label: `${a.Apellidos}, ${a.Nombres} (${a.IdAlumno})`,
                }))}
                notFoundContent={loadingLista ? <Spin size="small" /> : 'Sin resultados'}
                allowClear
                onClear={resetTodo}
              />
            </Col>
            {/* Nombre inline */}
            {alumno && (
              <Col xs={24} sm={8} md={12}>
                <Space wrap>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  <Text strong style={{ fontSize: 14 }}>{alumno.NombreAlumno}</Text>
                  <Tag color="blue" style={{ fontSize: 12 }}>Carnet: {alumno.IdAlumno}</Tag>
                  {alumno.NombreGrado && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {alumno.NombreGrado}
                      {alumno.NombreSeccion ? ` — Sección "${alumno.NombreSeccion}"` : ''}
                      {alumno.NombreJornada ? ` — ${alumno.NombreJornada}` : ''}
                    </Text>
                  )}
                </Space>
              </Col>
            )}
          </Row>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            PASO 2 — Elegir curso
        ══════════════════════════════════════════════════════════════════ */}
        {alumno && cursos.length > 0 && (
          <>
            <Divider style={{ margin: '8px 0 12px' }} />
            <div style={{ marginBottom: 8, fontWeight: 600, fontSize: 13, color: '#555' }}>
              PASO 2 — Elegir curso
            </div>
            <Row gutter={12} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={18} md={14} lg={10}>
                <Select
                  placeholder="Seleccioná un curso"
                  style={{ width: '100%' }}
                  value={cursoSel}
                  onChange={handleCursoChange}
                  showSearch
                  optionFilterProp="children"
                >
                  {cursos.map(c => (
                    <Option key={c.IdAsignacionDocente} value={c.IdAsignacionDocente}>
                      <Space size={4}>
                        <BookOutlined />
                        {c.NombreCurso}
                        {c.NombreDocente && (
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            — {c.NombreDocente}
                          </Text>
                        )}
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            PASO 3 — Unidad y actividad
        ══════════════════════════════════════════════════════════════════ */}
        {cursoSel && (
          <>
            <Divider style={{ margin: '8px 0 12px' }} />
            <div style={{ marginBottom: 8, fontWeight: 600, fontSize: 13, color: '#555' }}>
              PASO 3 — Unidad y actividad
            </div>
            <Spin spinning={loadingUnidades || loadingActividades}>
              <Space wrap style={{ marginBottom: 16 }}>
                <div>
                  <div style={{ marginBottom: 4, fontSize: 12, color: '#888' }}>Unidad</div>
                  <Select
                    placeholder="Seleccioná una unidad"
                    style={{ width: 240 }}
                    value={unidadSel}
                    onChange={handleUnidadChange}
                    loading={loadingUnidades}
                  >
                    {unidades.map(u => (
                      <Option key={u.IdUnidad} value={u.IdUnidad}>
                        {u.NombreUnidad}{tagUnidad(u)}
                      </Option>
                    ))}
                  </Select>
                </div>

                {unidadSel && (
                  <div>
                    <div style={{ marginBottom: 4, fontSize: 12, color: '#888' }}>Actividad</div>
                    <Select
                      placeholder="Seleccioná una actividad"
                      style={{ width: 290 }}
                      value={actividadSel}
                      onChange={handleActividadChange}
                      loading={loadingActividades}
                    >
                      {actividades.map(a => (
                        <Option key={a.IdActividad} value={a.IdActividad}>
                          <Space size={4}>
                            {a.NombreActividad}
                            <Tag
                              color={a.TipoActividad === 'zona' ? 'blue' : 'purple'}
                              style={{ fontSize: 10, margin: 0 }}
                            >
                              {a.TipoActividad === 'zona' ? 'Zona' : 'Final'}
                            </Tag>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {a.PunteoMaximo} pts
                            </Text>
                          </Space>
                        </Option>
                      ))}
                    </Select>
                  </div>
                )}
              </Space>

              {unidadSel && actividades.length === 0 && !loadingActividades && (
                <Alert
                  message="No hay actividades activas en esta unidad"
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                />
              )}
            </Spin>
          </>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            PASO 4 — Rectificación
            Aparece cuando actividadSel está elegido y terminó de cargar
        ══════════════════════════════════════════════════════════════════ */}
        {actividadSel && (
          <>
            <Divider style={{ margin: '8px 0 12px' }} />
            <div style={{ marginBottom: 12, fontWeight: 600, fontSize: 13, color: '#555' }}>
              PASO 4 — Rectificación
            </div>

            <Spin spinning={loadingNota}>
              {formularioCargado && (
                <>
                  {/* Aviso si el alumno no tenía nota previa */}
                  {notaActual === null && (
                    <Alert
                      type="info"
                      showIcon
                      message="Este alumno no tiene nota registrada en esta actividad. Podés ingresarla ahora."
                      style={{ marginBottom: 12 }}
                    />
                  )}

                  {/* Info de la actividad */}
                  <Descriptions
                    size="small"
                    bordered
                    column={{ xs: 1, sm: 2, md: 4 }}
                    style={{ marginBottom: 16 }}
                  >
                    <Descriptions.Item label="Alumno" span={2}>
                      <strong>{alumno?.NombreAlumno}</strong>
                      {alumno?.IdAlumno && (
                        <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                          Carnet: {alumno.IdAlumno}
                        </Text>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Actividad">
                      <strong>{actividadActual?.NombreActividad}</strong>
                    </Descriptions.Item>
                    <Descriptions.Item label="Tipo">
                      <Tag color={actividadActual?.TipoActividad === 'zona' ? 'blue' : 'purple'}>
                        {actividadActual?.TipoActividad === 'zona' ? 'Zona' : 'Final'}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Punteo máximo">
                      {punteoMax} pts
                    </Descriptions.Item>
                    <Descriptions.Item label="Nota actual">
                      {notaActual?.Punteo !== null && notaActual?.Punteo !== undefined ? (
                        <strong>{notaActual.Punteo}</strong>
                      ) : (
                        <Tag color="default">Sin nota</Tag>
                      )}
                    </Descriptions.Item>
                  </Descriptions>

                  {/* Campos del formulario */}
                  <Row gutter={[16, 12]} style={{ marginBottom: 16 }}>
                    <Col xs={24} sm={8} md={5}>
                      <div style={{ marginBottom: 4, fontWeight: 500 }}>
                        Nuevo punteo <Text type="danger">*</Text>
                      </div>
                      <InputNumber
                        min={0}
                        max={punteoMax}
                        step={0.25}
                        precision={2}
                        value={nuevoPunteo}
                        onChange={setNuevoPunteo}
                        style={{ width: '100%' }}
                        placeholder={`0 — ${punteoMax}`}
                        size="large"
                      />
                    </Col>
                    <Col xs={24} sm={16} md={11}>
                      <div style={{ marginBottom: 4, fontWeight: 500 }}>Observaciones</div>
                      <TextArea
                        rows={1}
                        value={observaciones}
                        onChange={e => setObservaciones(e.target.value)}
                        placeholder="Observaciones opcionales sobre la nota"
                        maxLength={300}
                      />
                    </Col>
                  </Row>

                  <Row gutter={[16, 12]} style={{ marginBottom: 20 }}>
                    <Col xs={24} md={16}>
                      <div style={{ marginBottom: 4, fontWeight: 500 }}>
                        Motivo de la rectificación <Text type="danger">*</Text>
                      </div>
                      <TextArea
                        rows={2}
                        value={motivo}
                        onChange={e => setMotivo(e.target.value)}
                        placeholder="Explicá brevemente por qué se rectifica esta nota (mínimo 5 caracteres)"
                        maxLength={500}
                        showCount
                        status={motivo.length > 0 && motivo.trim().length < 5 ? 'error' : ''}
                      />
                    </Col>
                  </Row>

                  <div style={{ textAlign: 'right', marginBottom: 8 }}>
                    <Button
                      type="primary"
                      size="large"
                      icon={<SaveOutlined />}
                      loading={guardando}
                      onClick={confirmarGuardar}
                      style={{ minWidth: 220 }}
                    >
                      Guardar Rectificación
                    </Button>
                  </div>
                </>
              )}
            </Spin>
          </>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            RESULTADO
        ══════════════════════════════════════════════════════════════════ */}
        {resultado && (
          <>
            <Divider style={{ margin: '20px 0 16px' }} />
            <Alert
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              message="Nota rectificada — Nota de unidad recalculada automáticamente"
              style={{ marginBottom: 16 }}
            />
            <Card
              size="small"
              style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}
              title={
                <Space>
                  <TrophyOutlined style={{ color: '#389e0d' }} />
                  Resultado — {cursoActual?.NombreCurso} · {alumno?.NombreAlumno}
                </Space>
              }
            >
              <Row gutter={16} style={{ textAlign: 'center' }}>
                <Col span={6}>
                  <Statistic
                    title="Nota zona"
                    value={resultado.notaUnidad?.NotaZona ?? '—'}
                    suffix="pts"
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Nota final"
                    value={resultado.notaUnidad?.NotaFinal ?? '—'}
                    suffix="pts"
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Nota total unidad"
                    value={resultado.notaUnidad?.NotaTotal ?? '—'}
                    suffix="pts"
                    valueStyle={{ color: '#1677ff', fontWeight: 700 }}
                  />
                </Col>
                <Col span={6}>
                  {resultado.notaUnidad?.Aprobado ? (
                    <Statistic
                      title="Estado"
                      value="Aprobado"
                      prefix={<CheckCircleOutlined />}
                      valueStyle={{ color: '#389e0d' }}
                    />
                  ) : (
                    <Statistic
                      title="Estado"
                      value="Reprobado"
                      prefix={<ClockCircleOutlined />}
                      valueStyle={{ color: '#cf1322' }}
                    />
                  )}
                </Col>
              </Row>
              <div style={{ marginTop: 12, color: '#888', fontSize: 12 }}>
                Solicitud #{resultado.IdSolicitud}
                {resultado.calificacion?.IdCalificacion &&
                  ` · Calificación #${resultado.calificacion.IdCalificacion}`}
              </div>
            </Card>
          </>
        )}
      </Card>
    </div>
  );
};

export default ActividadesDirector;
